import React, { useState, useEffect } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSlider } from '../context/SliderContext'
import { getImageUrl } from '../utils/imageUtils'

const Hero = () => {
  const { t } = useTranslation()
  const [typedText, setTypedText] = useState('')
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [activeSlides, setActiveSlides] = useState([])
  const fullText = t('hero.mainTagline')
  
  // Get slider data from context
  const { sliderItems, loading } = useSlider()

  // Filter and sort active slides
  useEffect(() => {
    const active = sliderItems
      .filter(slide => slide.isActive)
      .sort((a, b) => a.order - b.order)
    setActiveSlides(active)
  }, [sliderItems])

  // Map active slides to image URLs
  const backgroundImages = activeSlides.map(slide => getImageUrl(slide.image))

  // Get current slide data for dynamic content
  const currentSlide = activeSlides[currentImageIndex] || {
    caption: t('hero.mainTagline'),
    buttonText: t('hero.exploreButton'),
    buttonLink: '#about'
  }

  // Typing effect for tagline
  useEffect(() => {
    let index = 0
    const timer = setInterval(() => {
      if (index <= fullText.length) {
        setTypedText(fullText.slice(0, index))
        index++
      } else {
        clearInterval(timer)
      }
    }, 100)

    return () => clearInterval(timer)
  }, [])

  const scrollToNext = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  // Auto-slide effect
  useEffect(() => {
    if (backgroundImages.length <= 1) {
      setCurrentImageIndex(0)
      return undefined
    }

    const imageTimer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length)
    }, 5000)

    return () => clearInterval(imageTimer)
  }, [backgroundImages.length])

  // Navigation functions
  const nextImage = () => {
    if (backgroundImages.length === 0) {
      return
    }
    setCurrentImageIndex((prevIndex) => 
      (prevIndex + 1) % backgroundImages.length
    )
  }

  const prevImage = () => {
    if (backgroundImages.length === 0) {
      return
    }
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? backgroundImages.length - 1 : prevIndex - 1
    )
  }

  // Show loading state or empty state
  if (loading) {
    return (
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 bg-gradient-to-br from-primary-purple to-primary-magenta">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-xl font-montserrat">{t('common.loading')}</p>
        </div>
      </section>
    )
  }

  if (activeSlides.length === 0) {
    return (
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 bg-gradient-to-br from-primary-purple to-primary-magenta">
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <h1 className="text-4xl sm:text-5xl opacity-50 lg:text-6xl font-bold text-white font-poppins mb-20 text-shadow-lg">
              {t('hero.schoolName')}
            </h1>
            <p className="text-2xl sm:text-4xl mb-4 text-white opacity-80 font-medium text-shadow">
              {t('hero.trustNameGu')}
            </p>
            <p className="text-2xl sm:text-4xl text-white/90 opacity-80 mt-1 mb-20 text-shadow">
              {t('hero.trustNameEn')}
            </p>
          </div>
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl opacity-50 font-semibold text-white font-montserrat text-shadow">
              {typedText}
              <span className="animate-pulse">|</span>
            </h2>
          </div>
          <div className="flex opacity-50 flex-col sm:flex-row gap-4 justify-center items-center">
            <button 
              onClick={() => document.getElementById('admissions')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto bg-white text-primary-purple px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/90 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-center cursor-pointer"
            >
              {t('hero.admissionOpen')}
            </button>
            <button 
              onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto bg-white/20 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/30 hover:scale-105 transition-all duration-300 text-center cursor-pointer"
            >
              {t('hero.exploreButton')}
            </button>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
      {/* Dynamic background slider */}
      <div className="absolute inset-0">
        {backgroundImages.map((image, index) => {
          const slide = activeSlides[index]
          const overlayOpacity = slide?.overlayOpacity || 20
          
          return (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ 
                backgroundImage: `url(${image})` 
              }}
            >
              <div 
                className="absolute inset-0 bg-black"
                style={{ opacity: overlayOpacity / 100 }}
              ></div>
            </div>
          )
        })}
      </div> 
      
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Content based on current slide */}
        <div className="animate-fade-in">
          {/* School Name - Show on all slides */}
          <div className="mb-6 animate-slide-in">
            <h1 className="text-4xl sm:text-5xl opacity-50 lg:text-6xl font-bold text-white font-poppins mb-8 text-shadow-lg">
              {t('hero.schoolNameNormal')}
            </h1>
            <p className="text-xl sm:text-2xl mb-4 text-white opacity-80 font-medium text-shadow">
              {t('hero.trustNameGu')}
            </p>
            <p className="text-xl sm:text-2xl text-white/90 opacity-80 mb-8 text-shadow">
              {t('hero.trustNameEn')}
            </p>
          </div>

          {/* Content that shows on all slides with consistent styling */}
          {currentImageIndex === 0 ? (
            // First slide - Show animated tagline and main CTA buttons
            <>
              {/* Animated Tagline */}
              <div className="mb-8 h-12 flex items-center justify-center">
                <h2 className="text-2xl sm:text-3xl opacity-50 lg:text-4xl font-semibold text-white font-montserrat text-shadow">
                  {typedText}
                  <span className="animate-pulse">|</span>
                </h2>
              </div>        
              
              {/* CTA Buttons */}
              <div className="flex flex-col opacity-50 sm:flex-row gap-4 justify-center items-center mb-8">
                <button 
                  onClick={() => document.getElementById('admissions')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto bg-primary-purple text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-magenta hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-center cursor-pointer"
                >
                  {t('hero.admissionOpen')}
                </button>
                <button 
                  onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto bg-white/90 backdrop-blur-sm border-2 border-white text-primary-purple px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:scale-105 transition-all duration-300 text-center cursor-pointer"
                >
                  {t('hero.exploreButton')}
                </button>
              </div>
            </>
          ) : (
            // Other slides - Show slide caption with same styling and two buttons like first slide
            <>
              {/* Slide Caption with same opacity styling */}
              <div className="mb-8 h-12 flex items-center justify-center">
                <h2 className="text-2xl sm:text-3xl opacity-50 lg:text-4xl font-semibold text-white font-montserrat text-shadow">
                  {currentSlide.caption || t('hero.mainTagline')}
                </h2>
              </div>

              {/* Same CTA Buttons as first slide */}
              <div className="flex flex-col opacity-50 sm:flex-row gap-4 justify-center items-center mb-8">
                <button 
                  onClick={() => document.getElementById('admissions')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto bg-primary-purple text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-magenta hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-center cursor-pointer"
                >
                  {t('hero.admissionOpen')}
                </button>
                <button 
                  onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto bg-white/90 backdrop-blur-sm border-2 border-white text-primary-purple px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:scale-105 transition-all duration-300 text-center cursor-pointer"
                >
                  {t('hero.exploreButton')}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevImage}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm border border-white/30 text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 group z-20"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
        
        <button
          onClick={nextImage}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm border border-white/30 text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 group z-20"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
      </div>

      {/* Simple Slider Dots - Positioned at Bottom */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex justify-center items-center">
          {/* Small dot indicators */}
          <div className="flex items-center space-x-3">
            {backgroundImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentImageIndex 
                    ? 'bg-white shadow-lg scale-125' 
                    : 'bg-white/40 hover:bg-white/70 hover:scale-110'
                }`}
                aria-label={`View campus image ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero